import { showAppAlert } from '../common/appDialog';
import { useState } from 'react';
import { db, auth } from '../../firebaseConfig';
import {
    collection,
    addDoc,
    getDocs,
    doc,
    runTransaction,
    arrayUnion,
    query,
    where,
} from 'firebase/firestore';
import { sanitizeFirestoreData } from '../components/reports/ReportShared';
import type { SrmProject, ActiveFramework } from '@types';
import { reportSrmModules, reportSrmProjects, type ReportSaveScope } from '../common/analysisPersistence';
import { libraryReportId, reportContentKey, reportMatchesScope } from '../common/reportIdentity';

export type SaveResult =
    | { success: true; existing?: 'library' | 'srm'; location?: string }
    | { success: false; error: string };

/**
 * Custom hook to manage the persistence layer for intelligence reports.
 * Handles saving to Library, SRM Projects, Frameworks, and routing for review.
 */
export function useAnalysisPersistence() {
    const [isSaving, setIsSaving] = useState(false);
    const [isReviewing, setIsReviewing] = useState(false);

    /**
     * Save a report to the Library Vault.
     */
    const handleSaveToLibrary = async (
        finalReport: any,
        organizationId: string,
        clientId: string | null
    ): Promise<SaveResult> => {
        if (!db) return { success: false, error: 'No database connection.' };
        if (!auth?.currentUser)
            return { success: false, error: 'Please sign in again before archiving.' };
        if (!organizationId)
            return { success: false, error: 'Organization context is missing. Reopen this report and retry.' };
        setIsSaving(true);
        try {
            const scope = { ...finalReport, organizationId, clientId };
            const content = reportContentKey(finalReport);
            if (!content)
                return { success: false, error: 'This report has no readable content to save.' };
            const reportRef = doc(db, 'saved_intel', await libraryReportId(content, scope));
            const [saved, delivered, projects, frameworks] = await Promise.all([
                getDocs(query(collection(db, 'saved_intel'), where('organizationId', '==', organizationId))),
                getDocs(query(collection(db, 'generated_reports'), where('organizationId', '==', organizationId), where('deliverySnapshot', '==', true))),
                scope.assetId || scope.srmProjectId
                    ? getDocs(query(collection(db, 'srm_projects'), where('organizationId', '==', organizationId))) : Promise.resolve({ docs: [] }),
                scope.assetId || scope.srmProjectId
                    ? getDocs(query(collection(db, 'active_frameworks'), where('organizationId', '==', organizationId))) : Promise.resolve({ docs: [] }),
            ]);
            const matchingProjects = projects.docs.filter(snapshot => reportMatchesScope({ ...snapshot.data(), srmProjectId: snapshot.id }, scope));
            const projectIds = new Set(matchingProjects.map(snapshot => snapshot.id));
            const matchingFrameworks = frameworks.docs.filter(snapshot => projectIds.has(snapshot.data().projectId));
            const matches = (value: any) => reportMatchesScope(value, scope) && reportContentKey(value) === content;
            const libraryMatches = [...saved.docs.filter(snapshot => snapshot.data().isMapping !== true), ...delivered.docs].filter(snapshot => matches(snapshot.data()));
            // Querying by tenant permits empty results; missing documents cannot be read under tenant rules.
            // A content-derived destination also keeps concurrent first saves in the same document.
            return await runTransaction(db, async transaction => {
                const [existingLibrary, currentProjects, currentFrameworks] = await Promise.all([
                    Promise.all(libraryMatches.map(snapshot => transaction.get(snapshot.ref))),
                    Promise.all(matchingProjects.map(snapshot => transaction.get(snapshot.ref))),
                    Promise.all(matchingFrameworks.map(snapshot => transaction.get(snapshot.ref))),
                ]);
                if (existingLibrary.some(snapshot => snapshot.exists() && matches(snapshot.data())))
                    return { success: true, existing: 'library' } as SaveResult;
                const currentProjectIds = new Set<string>();
                for (const snapshot of currentProjects) {
                    const project = snapshot.data() as SrmProject | undefined;
                    if (!project || !reportMatchesScope({ ...project, srmProjectId: snapshot.id }, scope))
                        continue;
                    currentProjectIds.add(snapshot.id);
                    if ((project.modules || []).some(module => reportContentKey(module.resultData ?? (module as any).data) === content))
                        return { success: true, existing: 'srm', location: project.name || project.projectName } as SaveResult;
                }
                for (const snapshot of currentFrameworks) {
                    const framework = snapshot.data() as ActiveFramework | undefined;
                    if (framework?.organizationId === organizationId && framework.projectId && currentProjectIds.has(framework.projectId)
                        && (framework.components || []).some(component => reportContentKey(component.data) === content))
                        return { success: true, existing: 'srm', location: framework.name } as SaveResult;
                }
                transaction.set(reportRef, sanitizeFirestoreData({
                    ...finalReport, userId: auth?.currentUser?.uid || '', organizationId, clientId: clientId || null,
                    savedAt: new Date().toISOString(), type: finalReport.reportType || 'Unknown', isMapping: false,
                }));
                return { success: true } as SaveResult;
            });
        } catch (e) {
            console.error('Error saving to library:', e);
            return { success: false, error: 'Failed to save report to library.' };
        } finally {
            setIsSaving(false);
        }
    };

    const getActiveSrmProjects = async (scope: ReportSaveScope): Promise<SrmProject[]> => {
        if (!db || !scope.organizationId)
            return [];

        const projectsQuery = query(
            collection(db, 'srm_projects'),
            where('organizationId', '==', scope.organizationId)
        );
        const snapshot = await getDocs(projectsQuery);

        return reportSrmProjects(snapshot.docs.map(project => ({ ...project.data(), id: project.id }) as SrmProject), scope)
            .map(project => ({ ...project, modules: reportSrmModules(project, scope) }))
            .filter(project => project.modules.length > 0)
            .sort((left, right) => (left.name || left.projectName || '').localeCompare(right.name || right.projectName || ''));
    };

    const handleSaveToSrm = async (
        finalReport: any,
        targetId: string,
        moduleId: string,
        scope: ReportSaveScope
    ) => {
        if (!db || !auth?.currentUser)
            return false;
        setIsSaving(true);
        let stage = 'loading SRM target';
        try {
            if (!scope.organizationId || (!scope.assetId && !scope.srmProjectId))
                throw new Error('This report has no linked facility. Open it from the facility SRM framework.');
            const projectRef = doc(db, 'srm_projects', targetId);
            const frameworks = await getDocs(query(collection(db, 'active_frameworks'),
                where('organizationId', '==', scope.organizationId), where('projectId', '==', targetId)));
            const savedAt = new Date().toISOString();
            const user = auth.currentUser.email || auth.currentUser.uid;
            stage = 'updating SRM framework';
            const alreadySaved = await runTransaction(db, async transaction => {
                const projectSnap = await transaction.get(projectRef);
                if (!projectSnap.exists())
                    throw new Error('No SRM framework exists for this facility.');
                const project = { ...projectSnap.data(), id: projectSnap.id } as SrmProject;
                if (!reportSrmProjects([project], scope).length)
                    throw new Error('The SRM framework no longer matches this facility. Reopen the report.');
                if (project.modules.filter(module => module.id === moduleId).length !== 1
                    || !reportSrmModules(project, scope).some(module => module.id === moduleId))
                    throw new Error('The matching SRM report module is missing. Reopen the framework.');
                const linkedFrameworks = await Promise.all(frameworks.docs.map(framework => transaction.get(framework.ref)));
                const savedReport = sanitizeFirestoreData({ ...finalReport, organizationId: scope.organizationId,
                    clientId: project.clientId || null, assetId: project.assetId, srmProjectId: targetId,
                    srmModuleId: moduleId, workflowId: project.modules.find(module => module.id === moduleId)?.workflowId });
                const content = reportContentKey(savedReport);
                const currentModule = project.modules.find(module => module.id === moduleId)!;
                const projectMatches = reportContentKey(currentModule.resultData ?? (currentModule as any).data) === content;
                for (const snapshot of linkedFrameworks) {
                    const framework = snapshot.data() as ActiveFramework | undefined;
                    if (!framework || framework.organizationId !== scope.organizationId || framework.projectId !== targetId
                        || framework.components?.filter(component => component.id === moduleId).length !== 1)
                        throw new Error('The deployed SRM report module is missing. Reopen the framework.');
                }
                if (projectMatches && linkedFrameworks.every(snapshot => reportContentKey(
                    (snapshot.data() as ActiveFramework).components.find(component => component.id === moduleId)?.data) === content))
                    return true;
                for (const snapshot of linkedFrameworks) {
                    const framework = snapshot.data() as ActiveFramework;
                    if (reportContentKey(framework.components.find(component => component.id === moduleId)?.data) === content)
                        continue;
                    transaction.update(snapshot.ref, sanitizeFirestoreData({
                        components: framework.components.map(component => component.id === moduleId
                            ? { ...component, data: savedReport, status: 'Active', lastUpdated: savedAt,
                                history: [...(component.history || []), { timestamp: savedAt, user, data: savedReport }] }
                            : component),
                        lastReview: savedAt,
                    }));
                    transaction.update(snapshot.ref, { auditLog: arrayUnion({ id: `intel-${Date.now()}`,
                        timestamp: savedAt, user, action: 'Intelligence Mapping',
                        details: `Updated component ${moduleId} with ${finalReport.reportType || 'Intelligence Report'}` }) });
                }
                if (!projectMatches)
                    transaction.update(projectRef, sanitizeFirestoreData({ updatedAt: savedAt,
                        modules: project.modules.map(module => module.id === moduleId
                            ? { ...module, resultData: savedReport, status: 'Ready' } : module) }));
                return false;
            });
            void showAppAlert(alreadySaved ? 'This report is already saved in this SRM framework.' : 'Report successfully attached to SRM!');
            return true;
        } catch (e) {
            console.error(`Error ${stage}:`, e);
            void showAppAlert(e instanceof Error ? e.message : `Failed while ${stage}.`);
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleSendForReview = async (finalReport: any, reviewerEmail: string, notes: string, organizationId: string) => {
        if (!db) return;
        setIsReviewing(true);
        try {
            await addDoc(collection(db, 'plan_reviews'), sanitizeFirestoreData({
                planData: finalReport,
                reviewerEmail,
                notes,
                requestedBy: auth?.currentUser?.email || auth?.currentUser?.uid || 'Unknown',
                organizationId,
                status: 'Pending Review',
                createdAt: new Date().toISOString(),
                type: finalReport.reportType || 'CrisisManagementPlan',
            }));
            void showAppAlert(`Plan successfully routed to ${reviewerEmail} for formal review.`);
            return true;
        } catch (e) {
            console.error('Error routing plan for review', e);
            void showAppAlert('Failed to route plan for review.');
            return false;
        } finally {
            setIsReviewing(false);
        }
    };

    return {
        isSaving,
        isReviewing,
        handleSaveToLibrary,
        handleSaveToSrm,
        handleSendForReview,
        getActiveSrmProjects,
    };
}
